import { FC, ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { u } from '../common/Units';
import { AppContext } from '../contexts/AppContext';
import { getTranslation } from '../common/Translation';
import PlayIconSvg from "../assets/icon_play.svg?react";
import StopIconSvg from "../assets/icon_stop.svg?react";
import BlockContainer from './BlockContainer';
import useFetchMedia from '../hooks/useFetchMedia';
import { MEDIA_PREFIX } from '../Settings';

interface BlockAudioWrapperProps {
  children?: ReactElement | ReactElement[]
}

interface BlockAudioItemProps {
  media?: any,
  layout?: "wide" | "block",
  children?: ReactElement | ReactElement[]
}

const Wrapper = styled.ul`
  list-style: none;
  float: left;
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;

  &:empty {
    display: none;
  }
`;

const AudioItem = styled.li`
  margin: 0;
  float: left;
  padding: ${u(29)};
  border-radius: var(--block-radius);
  width: 100%;
  display: flex;
  align-items: center;
  background-color: ${p => p.theme.blockColor};

  &:not(:last-child) {
    margin-bottom: ${u(7)};
  }

  &.block {
    width: ${u(109)};
    min-width: ${u(109)};
    height: ${u(109)};
    justify-content: center;
    position: relative;

    & button::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: ${u(51)};
    }

    & span, & div {
      display: none;
    }
  }
`;

const Caption = styled.span`
  --max-lines: 1;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  font-size: 1.2em;
`;


const Button = styled.button`
  width: ${u(58)};
  min-width: ${u(58)};
  height: ${u(58)};
  border-radius: ${u(29)};
  margin-right: ${u(29)};
  background-color: ${p => p.theme.accentColor};
  border: none;
  float: left;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  pointer-events: all;

  &:active {
    opacity: .8;
  }
`;

const AudioElement = styled.audio`
  display: none;
`;

const PlayIcon = styled(PlayIconSvg)`
  margin-left: ${u(5)};
  height: ${u(29)};
  width: ${u(22)};
  color: ${p => p.theme.backgroundColor};
`

const StopIcon = styled(StopIconSvg)`
  height: ${u(22)};
  width: ${u(22)};
  color: ${p => p.theme.backgroundColor};
`

const Control = styled.div`
  width: 100%;
`;

const ProgressBar = styled.div`
  width: 100%;
  background-color: #FFFFFF15;
  height: ${u(4)};
  border-radius: 2px;
  margin-top: ${u(11)};
  position: relative;

  &::before {
    width: 100%;
    content: "";
    position: absolute;
    padding: ${u(22)} 0;
    transform: translateY(-50%);
  }
`;

const Progress = styled.div`
  width: 0;
  height: ${u(4)};
  border-radius: 2px;
  background-color: ${p => p.theme.accentColor};
  transition: width .1s;
  pointer-events: none;
`;

/**
 * Render an audio block container
 * @returns {JSX.Element} Component template
 */
export const BlockAudioWrapper: FC<BlockAudioWrapperProps> = ({children}) => {
  return (
    <BlockContainer>
      <Wrapper>{children}</Wrapper>
    </BlockContainer>
  );
}



/**
 * Render an audio block item
 * @returns {JSX.Element} Component template
 */
export const BlockAudioItem: FC<BlockAudioItemProps> = ({media, layout, children}) => {
  const { currentLanguage, globalVolume, resetTimer } = useContext(AppContext);
  const [playbackPosition, setPlaybackPosition] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const audioElement = useRef<HTMLAudioElement>(null);
  const [itemAudio] = useFetchMedia(media);

  /**
   * Stop all audio
   */
   const stopAllAudio = useCallback(() => {
    const audioElements = document.querySelectorAll("audio");
    audioElements.forEach(el => {
      el.pause();
      el.currentTime = 0;
    });
  }, []);

  /**
   * Toggle audio placback
   */
   const togglePlay = useCallback(() => {
    if (isPlaying) {
      if (audioElement.current) {
        audioElement.current.pause();
        audioElement.current.currentTime = 0;
      }
      setIsPlaying(false);
    } else {
      stopAllAudio();
      if (audioElement.current) {
        audioElement.current.play();
        audioElement.current.currentTime = 0;
      }
      setIsPlaying(true);
    }
  }, [isPlaying, stopAllAudio]);

  useEffect(() => {
    if (audioElement.current && globalVolume !== undefined) {
      audioElement.current.volume = globalVolume;
    }
  }, [globalVolume]);

  // Listen for audio player time change
  useEffect(() => {
    const el = audioElement.current;
    if (!el) { return; }

    const onTimeUpdate = () => {
      setPlaybackPosition(Number((el.currentTime || 0) / (el.duration || 0)) * 100);
      resetTimer && resetTimer();
    };
    const onEnded = () => {
      setIsPlaying(false);
      el.currentTime = 0;
      resetTimer && resetTimer();
    };
    const onPause = () => { setIsPlaying(false); };

    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("ended", onEnded);
    el.addEventListener("pause", onPause);
    return () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("pause", onPause);
    };
  }, [audioElement, resetTimer]);

  /**
   * Update playback position on click
   */
  const updatePosition = useCallback((e: any) => {
    if (isPlaying && audioElement.current) {
      const boundingRect = e.target.getBoundingClientRect();
      audioElement.current.currentTime = Number((((e?.clientX - boundingRect?.left)) / (e?.target?.offsetWidth)) * audioElement.current.duration);
    }
  }, [isPlaying]);

  return (
    <AudioItem className={`${layout || ""} showInPreview`}>
      <AudioElement ref={audioElement} src={itemAudio?.url ? `${MEDIA_PREFIX}${itemAudio?.url}` : undefined}/>
      <Button onClick={togglePlay}>{isPlaying ? (<StopIcon/>) : (<PlayIcon/>)}</Button>
      <Control>
        {itemAudio?.title && (<Caption>{getTranslation(itemAudio?.title, currentLanguage)}</Caption>)}
        <ProgressBar onClick={updatePosition}><Progress style={{width: `${playbackPosition}%`}}/></ProgressBar>
      </Control>
      {children}
    </AudioItem>
  );
}